<script setup lang="ts">
import { computed, ref } from 'vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/tooltip'

const props = withDefaults(defineProps<{
  targetLabel: string
  blockedReason?: string | null
  blockedText?: string | null
}>(), {
  blockedReason: null,
  blockedText: null
})

const emit = defineEmits<{
  generate: []
}>()

const VISIBLE_LABEL = 'Generate assessment'

const isReasonOpen = ref(false)

const isBlocked = computed(() => Boolean(props.blockedReason || props.blockedText))

// A named reason wins over the save-first text: a goal generation cannot serve stays out
// of scope after saving, so sending the teacher off to save it would be a wasted trip.
const unavailableText = computed(() => {
  if (props.blockedReason) {
    return `This feature is not yet available for ${props.blockedReason}`
  }
  return props.blockedText ?? ''
})

// Starts with the visible text (WCAG 2.5.3) and carries the reason, since a screen reader
// user may never reach the tooltip.
const accessibleLabel = computed(() => {
  const labelWithTarget = `${VISIBLE_LABEL} for ${props.targetLabel}`
  if (!isBlocked.value) {
    return labelWithTarget
  }
  return `${labelWithTarget}. ${unavailableText.value}`
})

const stateClasses = computed(() => {
  if (isBlocked.value) {
    return 'cursor-default border-gray-700 text-gray-700'
  }
  return 'border-ai-bluePinkStart text-ai-bluePinkStart hover:border-blue-500 hover:bg-blue-200 hover:text-blue-500 focus:border-blue-500 focus:bg-blue-200 focus:text-blue-500'
})

// aria-disabled rather than disabled keeps the tile focusable, so a keyboard user can
// still reach the tooltip that explains why; the click is ignored here instead. The click
// opens the reason itself because reka-ui opens tooltips only on hover and keyboard focus,
// which a touch tap never produces.
function requestGeneration() {
  if (isBlocked.value) {
    isReasonOpen.value = true
    return
  }
  emit('generate')
}
</script>

<template>
  <TooltipProvider :delay-duration="0">
    <!-- Opens and stays open on click, which is when a teacher trying the blocked tile needs the reason. -->
    <Tooltip v-model:open="isReasonOpen" :disabled="!isBlocked" disable-closing-trigger>
      <TooltipTrigger as-child>
        <button
          type="button"
          data-testid="generate-assessment-tile"
          class="mb-4 mr-6 mt-3 flex h-[119px] w-44 items-center justify-center rounded-xs border-2 border-dashed bg-white px-2.5 py-3.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
          :class="stateClasses"
          :aria-label="accessibleLabel"
          :aria-disabled="isBlocked"
          @click="requestGeneration"
        >
          <span class="flex flex-col items-center gap-2.5" aria-hidden="true">
            <svg
              class="size-8 flex-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M4 20 15.5 8.5" />
              <path d="m15 4 1.4 3.1L19.5 8.5 16.4 9.9 15 13l-1.4-3.1L10.5 8.5 13.6 7.1z" />
              <path d="M6 3.5v2.6M4.7 4.8h2.6M19 16v2.4M17.8 17.2h2.4" />
            </svg>
            <span class="text-center text-md font-bold leading-tight">{{ VISIBLE_LABEL }}</span>
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" data-testid="generate-assessment-tile-tooltip">
        {{ unavailableText }}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
